import React, { useCallback, useEffect, useRef } from 'react';
import {
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody, Portal, useDisclosure,
} from '@chakra-ui/react';
import { useChatUi, useMessageContext } from '../ChatContext';
import { sourceThumbUrl } from '../api/config';
import PageImage from './PageImage';
import FileTile from './FileTile';

export function sourceLocation(source) {
  if (!source) return '';
  return [source.manual_label, source.page_label, source.printed_page].filter(Boolean).join(' · ');
}

/**
 * One citation pill. Hover (desktop) or tap/focus (touch, keyboard) shows a card with the page thumbnail,
 * location, section and snippet; a mouse click opens the page viewer at that source.
 */
export function CitationChip({ id }) {
  const { t } = useChatUi();
  const { sourceById, hoveredSourceId, setHoveredSourceId, openSource } = useMessageContext();
  const source = sourceById[id];
  const pointer = useRef('mouse');
  const buttonRef = useRef(null);
  // Controlled, so a tap can open the card; hover and focus still open and close it. A tap is followed by
  // emulated mouse events that make the hover trigger close the card again at once, so closes are ignored
  // briefly after a tap; a tap anywhere else closes it (touch browsers may never blur the button).
  const { isOpen, onOpen, onClose } = useDisclosure();
  const tapOpenedAt = useRef(0);
  const close = useCallback(() => {
    if (tapOpenedAt.current && Date.now() - tapOpenedAt.current < 600) return;
    tapOpenedAt.current = 0;
    onClose();
  }, [onClose]);
  useEffect(() => {
    if (!isOpen || !tapOpenedAt.current) return undefined;
    const onDown = (e) => {
      const target = e.target;
      if ((buttonRef.current && buttonRef.current.contains(target))
        || (target && target.closest && target.closest('.cv-cite-card'))) return;
      tapOpenedAt.current = 0;
      onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [isOpen, onClose]);
  if (!source) return null;

  const label = `${t('Source')} ${id}: ${[sourceLocation(source), source.section, source.section_title].filter(Boolean).join(' ')}`;

  return (
    <Popover
      trigger="hover"
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={close}
      openDelay={120}
      closeDelay={120}
      placement="top"
      isLazy
      gutter={6}
    >
      <PopoverTrigger>
        <button
          ref={buttonRef}
          type="button"
          className={`cite${hoveredSourceId === id ? ' is-active' : ''}`}
          aria-label={label}
          onPointerDown={(e) => { pointer.current = e.pointerType || 'mouse'; }}
          onMouseEnter={() => setHoveredSourceId(id)}
          onMouseLeave={() => setHoveredSourceId(null)}
          onFocus={() => setHoveredSourceId(id)}
          onBlur={() => setHoveredSourceId(null)}
          onClick={() => {
            if (pointer.current === 'mouse') openSource(id);
            else { // touch/pen: show the card; "Open page" in it opens the viewer
              tapOpenedAt.current = Date.now();
              onOpen();
            }
          }}
        >
          {id}
        </button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent className="cv-cite-card" width="300px">
          <PopoverArrow />
          <PopoverBody className="cv-cite-card__body">
            {source.kind === 'file'
              ? <FileTile name={source.manual_label} className="cv-cite-card__thumb" />
              : <PageImage src={sourceThumbUrl(source)} alt={sourceLocation(source)} ratio="3 / 4" className="cv-cite-card__thumb" />}
            <span className="cv-cite-card__text">
              <span className="cv-cite-card__loc">
                <span className="cv-badge">{id}</span>
                {sourceLocation(source)}
              </span>
              {(source.section || source.section_title) && (
                <span className="cv-cite-card__section">
                  {[source.section, source.section_title].filter(Boolean).join(' ')}
                </span>
              )}
              {source.snippet && <span className="cv-cite-card__snippet">{source.snippet}</span>}
              <button type="button" className="cv-link-btn" onClick={() => openSource(id)}>
                {t('Open page')}
              </button>
            </span>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

/** Adjacent markers ("[1][3]") render as one group of chips. */
export default function CitationGroup({ ids }) {
  const list = String(ids || '').split(',').map((s) => Number(s)).filter((n) => Number.isFinite(n));
  if (!list.length) return null;
  return (
    <span className="cv-cite-group">
      {list.map((id) => <CitationChip key={id} id={id} />)}
    </span>
  );
}
